import { useContext } from 'react';
import {
  Box,
  alpha,
  Stack,
  lighten,
  Divider,
  IconButton,
  Tooltip,
  styled,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import { SidebarContext } from 'src/contexts/SidebarContext';
import Logo from 'src/components/LogoSign';
import HeaderButtons from './Buttons';
import HeaderUserbox from './Userbox';
import HeaderMenu from './Menu';
import HeaderLanguage from './Userbox/HeaderLanguage';

const RIGHT_WIDTH = 180;

const HeaderWrapper = styled(Box)(
  ({ theme }) => `
    height: ${theme.header.height};
    color: ${theme.header.textColor};
    padding: ${theme.spacing(0, 2)};
    right: 0;
    z-index: 6;
    background-color: ${alpha(theme.header.background, 0.95)};
    backdrop-filter: blur(3px);
    position: fixed;
    width: 100%;
    left: 0;
    display: flex;
    align-items: center;
    box-sizing: border-box;
  `
);

const CenterArea = styled(Stack)(({ theme }) => ({
  flex: '1 1 auto',
  minWidth: 0,
  ...(theme.direction === 'rtl'
    ? { paddingLeft: `${RIGHT_WIDTH + 8}px` }
    : { paddingRight: `${RIGHT_WIDTH + 8}px` })
}));

const RightFixed = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  pointerEvents: 'auto',
  ...(theme.direction === 'rtl'
    ? { left: theme.spacing(2) }
    : { right: theme.spacing(2) })
}));

const IconSlot = styled(Box)(() => `
  min-width: 40px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  overflow: visible;
  > * { margin: 0 !important; }
`);

function Header() {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';
  const isLgUp = useMediaQuery(theme.breakpoints.up('sm'));

  /* ❌ CODE SUPPRIMÉ - Redondant avec Redux
  const [notifications, setNotifications] = useState([]);
  
  const fetchNotification = () => {
    const companyId = localStorage.getItem('company');
    const requests = [api.get('/notification/all')];
    
    if (companyId && companyId !== 'null') {
      requests.push(api.get(`/notification/${companyId}`));
    }

    Promise.all(requests)
      .then((responses) => {
        const allNotificationsData = responses[0]?.data || [];
        const companyNotificationsData = responses[1]?.data || [];
        
        const mergedNotifications = [
          ...allNotificationsData,
          ...companyNotificationsData
        ];
        
        setNotifications(mergedNotifications);
      })
      .catch((err) => console.log('Error fetching notifications:', err));
  };

  useEffect(() => {
    fetchNotification();
  }, []);
  */

  return (
    <HeaderWrapper
      sx={{
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 1px 0 ${alpha(
                lighten(theme.colors.primary.main, 0.7),
                0.15
              )}, 0px 2px 8px -3px rgba(0, 0, 0, 0.2), 0px 5px 22px -4px rgba(0, 0, 0, .1)`
            : `0px 2px 8px -3px ${alpha(
                theme.colors.alpha.black[100],
                0.2
              )}, 0px 5px 22px -4px ${alpha(
                theme.colors.alpha.black[100],
                0.1
              )}`
      }}
    >
      {isLgUp && (
        <Box
          mx={11}
          sx={{
            width: 50,
            marginLeft: isRTL ? 0 : theme.spacing(11),
            marginRight: isRTL ? theme.spacing(11) : 0,
            flex: '0 0 auto'
          }}
        >
          <Logo />
        </Box>
      )}

      <CenterArea
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        alignItems="center"
        spacing={2}
      >
        <HeaderMenu />
      </CenterArea>

      <RightFixed>
        <IconSlot>
          <HeaderButtons />
        </IconSlot>

        <IconSlot>
          <HeaderLanguage />
        </IconSlot>

        <Box sx={{ height: 38, display: 'flex', alignItems: 'center' }}>
          <HeaderUserbox />
        </Box>

        <Box
          component="span"
          sx={{
            ml: 0.5,
            display: { lg: 'none', xs: 'inline-flex' },
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto'
          }}
        >
          <Tooltip arrow title="Toggle Menu">
            <IconButton color="primary" onClick={toggleSidebar}>
              {!sidebarToggle ? (
                <MenuTwoToneIcon fontSize="small" />
              ) : (
                <CloseTwoToneIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </RightFixed>
    </HeaderWrapper>
  );
}

export default Header;
